<template>
    <div>
        <EditQuoteModal
            v-if="!searchDemoMode"
            :quoteId="quoteId"
            :currentQuoteType="currentQuoteType"
        />
        <div class="table-top-bar">
            <div class="legend-trigger">
                Legend
                <span tabindex="0" class="legend-trigger-wrapper">
                    <font-awesome-icon :icon="['fas', 'book-open']" />
                    <div class="legend-popover">
                        <p class="legend-title">Legend</p>
                        <ul>
                            <li v-if="!searchDemoMode" class="legend__research-quote">
                                <span class="status-pill status-pill--info">
                                    <span class="status-pill__icon">
                                        <font-awesome-icon :icon="['fas', 'flask']" /> </span
                                    >Research Quote
                                </span>
                            </li>
                            <li v-if="!searchDemoMode" class="legend__standard-quote">
                                <font-awesome-icon :icon="['fas', 'clipboard-list']" />
                                <span>Standard quote</span>
                            </li>
                            <li>
                                <span class="status-pill status-pill--positive">
                                    <span class="status-pill__icon">
                                        <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                                    </span>
                                    Search finished
                                </span>
                            </li>
                            <li>
                                <span class="status-pill status-pill--warning">
                                    <span class="status-pill__icon">
                                        <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                                    </span>
                                    Search not finished
                                </span>
                            </li>
                            <li v-if="!searchDemoMode">
                                <span class="status-pill status-pill--positive">
                                    <span class="status-pill__icon">
                                        <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
                                    </span>
                                    Quote finished
                                </span>
                            </li>
                            <li v-if="!searchDemoMode">
                                <span class="status-pill status-pill--warning">
                                    <span class="status-pill__icon">
                                        <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
                                    </span>
                                    Quote not finished
                                </span>
                            </li>
                            <li v-if="!searchDemoMode">
                                <span>Click on the Quote ID to view more details!</span>
                            </li>
                        </ul>
                    </div>
                </span>
            </div>
            <div class="table-summary text-muted small" v-if="displayedTotalItems !== null">
                <span v-if="displayedTotalItems > 0">
                    Showing <strong>{{ pageStart }}</strong
                    >-<strong>{{ pageEnd }}</strong> of
                    <strong>{{ displayedTotalItems }}</strong>
                    {{ searchDemoMode ? 'searches' : 'quotes' }}
                </span>
                <span v-else>No {{ searchDemoMode ? 'searches' : 'quotes' }} found</span>
            </div>
        </div>

        <table class="table table-striped table-class hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th class="select-header">
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="selectAll"
                                @change="toggleSelectAll"
                            />
                        </div>
                    </th>
                    <th>
                        <div class="sortable-header">
                            <span @click="sortBy('id')">ID</span>
                            <span v-if="sortColumn === 'id'" class="sort-indicator">
                                <font-awesome-icon
                                    v-if="sortDirection !== 'asc'"
                                    :icon="['fas', 'chevron-up']"
                                />
                                <font-awesome-icon
                                    v-if="sortDirection !== 'desc'"
                                    :icon="['fas', 'chevron-down']"
                                />
                            </span>
                        </div>
                        <div class="sort-buttons">
                            <button
                                @click="sortBy('search_finished')"
                                type="button"
                                class="btn btn-sort"
                            >
                                <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                            </button>
                            <span v-if="sortColumn === 'search_finished'" class="sort-indicator">
                                <font-awesome-icon
                                    v-if="sortDirection !== 'asc'"
                                    :icon="['fas', 'chevron-up']"
                                />
                                <font-awesome-icon
                                    v-if="sortDirection !== 'desc'"
                                    :icon="['fas', 'chevron-down']"
                                />
                            </span>
                            <button
                                v-if="!searchDemoMode"
                                @click="sortBy('quote_finished')"
                                type="button"
                                class="btn btn-sort"
                            >
                                <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
                            </button>
                            <span
                                v-if="!searchDemoMode && sortColumn === 'quote_finished'"
                                class="sort-indicator"
                            >
                                <font-awesome-icon
                                    v-if="sortDirection !== 'asc'"
                                    :icon="['fas', 'chevron-up']"
                                />
                                <font-awesome-icon
                                    v-if="sortDirection !== 'desc'"
                                    :icon="['fas', 'chevron-down']"
                                />
                            </span>
                        </div>
                    </th>
                    <th
                        v-if="!searchDemoMode"
                        class="sortable-header"
                        @click="sortBy('customer')"
                    >
                        <span>Customer</span>
                        <span v-if="sortColumn === 'customer'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th
                        v-if="!searchDemoMode"
                        class="sortable-header"
                        @click="sortBy('customer_ref')"
                    >
                        <span>Customer Ref</span>
                        <span v-if="sortColumn === 'customer_ref'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th
                        v-if="!searchDemoMode"
                        class="sortable-header"
                        @click="sortBy('due_date')"
                    >
                        <span>Due Date</span>
                        <span v-if="sortColumn === 'due_date'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th class="sortable-header">
                        <span>Part Number List</span>
                        <span v-if="sortColumn === 'part_number_list'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th class="sortable-header">
                        <span>Part and Sub List</span>
                        <span v-if="sortColumn === 'part_and_sub_list'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th class="sortable-header" @click="sortBy('time_created')">
                        <span>Time Created</span>
                        <span v-if="sortColumn === 'time_created'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th
                        v-if="!searchDemoMode"
                        class="sortable-header"
                        @click="sortBy('name')"
                    >
                        <span>Name</span>
                        <span v-if="sortColumn === 'name'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th
                        v-if="!searchDemoMode"
                        class="sortable-header"
                        @click="sortBy('total_value')"
                    >
                        <span>Total Value</span>
                        <span v-if="sortColumn === 'total_value'" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                    </th>
                    <th v-if="!searchDemoMode"><span>Actions</span></th>
                </tr>
            </thead>
            <tbody v-if="paginatedData.length > 0">
                <tr v-for="(row, index) in paginatedData" :key="index">
                    <td>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :value="row.id"
                                v-model="selectedIds"
                                @change="emitSelection"
                            />
                        </div>
                    </td>
                    <td>
                        <div class="id-cell">
                            <div
                                v-if="!searchDemoMode"
                                class="id-value"
                                :class="{ 'research-quote': isResearchQuote(row) }"
                                role="button"
                                tabindex="0"
                                @click="openDetails(row)"
                                @keydown.enter.prevent="openDetails(row)"
                                @keydown.space.prevent="openDetails(row)"
                            >
                                <span>{{ row.id }}</span>
                                <span
                                    v-if="isResearchQuote(row)"
                                    class="research-indicator"
                                    role="img"
                                    aria-label="Research Quote"
                                >
                                    <font-awesome-icon :icon="['fas', 'flask']" />
                                </span>
                            </div>
                            <div v-else class="id-value id-value--static">
                                <span>{{ row.id }}</span>
                            </div>
                            <div
                                class="btn-group btn-group-sm quote-status-group"
                                :class="{ 'quote-status-group--search-only': searchDemoMode }"
                                role="group"
                                :aria-label="searchDemoMode ? 'Search progress' : 'Quote progress'"
                            >
                                <button
                                    type="button"
                                    class="btn btn-status btn-search"
                                    :class="[statusButtonClass(row.search_finished), { 'btn-search-interactive': showLiveSearchStatus }]"
                                    :aria-disabled="showLiveSearchStatus ? null : 'true'"
                                    :tabindex="showLiveSearchStatus ? 0 : -1"
                                    :aria-label="showLiveSearchStatus ? `View live-search status for search ${row.id}` : statusLabel('Search', row.search_finished)"
                                    :title="showLiveSearchStatus ? 'View vendor search status' : statusLabel('Search', row.search_finished)"
                                    @click="showLiveSearchStatus && $emit('show-live-search-status', row)"
                                >
                                    <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                                </button>
                                <button
                                    v-if="!searchDemoMode"
                                    type="button"
                                    class="btn btn-status btn-quote"
                                    :class="statusButtonClass(row.quote_finished)"
                                    aria-disabled="true"
                                    tabindex="-1"
                                >
                                    <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
                                </button>
                            </div>
                        </div>
                    </td>
                    <td v-if="!searchDemoMode">{{ row.customer }}</td>
                    <td v-if="!searchDemoMode">{{ row.customer_ref }}</td>
                    <td v-if="!searchDemoMode">{{ formatDueDate(row.due_date) }}</td>
                    <td class="parts-list">
                        <template v-if="Array.isArray(row.part_number_list)">
                            <span
                                v-for="(item, i) in limitedItems(row, 'part_number_list')"
                                :key="`part-${i}`"
                            >
                                <span class="badge badge-danger">{{ item }}</span>
                                &nbsp;
                            </span>
                            <button
                                v-if="hasOverflow(row, 'part_number_list')"
                                class="link-button"
                                type="button"
                                @click="toggleExpanded(row, 'part_number_list')"
                            >
                                {{
                                    isExpanded(row, 'part_number_list')
                                        ? 'less'
                                        : `${hiddenItemCount(row, 'part_number_list')} more...`
                                }}
                            </button>
                            <span class="parts-total">
                                {{ listTotal(row, 'part_number_list') }} total
                            </span>
                        </template>
                        <span v-else>{{ row.part_number_list }}</span>
                    </td>
                    <td class="parts-list">
                        <template v-if="Array.isArray(row.part_and_sub_list)">
                            <span
                                v-for="(item, i) in limitedItems(row, 'part_and_sub_list')"
                                :key="`sub-${i}`"
                            >
                                <span class="badge badge-danger">{{ item }}</span>
                                &nbsp;
                            </span>
                            <button
                                v-if="hasOverflow(row, 'part_and_sub_list')"
                                class="link-button"
                                type="button"
                                @click="toggleExpanded(row, 'part_and_sub_list')"
                            >
                                {{
                                    isExpanded(row, 'part_and_sub_list')
                                        ? 'less'
                                        : `${hiddenItemCount(row, 'part_and_sub_list')} more...`
                                }}
                            </button>
                            <span class="parts-total">
                                {{ listTotal(row, 'part_and_sub_list') }} total
                            </span>
                        </template>
                        <span v-else>{{ row.part_and_sub_list }}</span>
                    </td>
                    <td>{{ formatAmericanDate(row.time_created) }}</td>
                    <td v-if="!searchDemoMode">{{ row.name }}</td>
                    <td v-if="!searchDemoMode">{{ formatQuoteValue(row.total_value) }}</td>
                    <td v-if="!searchDemoMode">
                        <a
                            href="javascript:;"
                            @click="triggerEditQuote(row.id, row.quote_type)"
                            class="mx-2"
                            title="Edit Quote"
                            style="color: #da1d62"
                        >
                            <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                        </a>
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr>
                    <td colspan="100%" class="text-center" style="padding: 20px">
                        No data available
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="d-flex flex-wrap justify-content-between align-items-center my-3 gap-3">
            <div class="d-flex align-items-center gap-2">
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div class="text-muted small flex-grow-1 text-center">
                <span v-if="displayedTotalItems > 0">
                    Showing <strong>{{ pageStart }}</strong
                    >-<strong>{{ pageEnd }}</strong> of
                    <strong>{{ displayedTotalItems }}</strong>
                    {{ searchDemoMode ? 'searches' : 'quotes' }}
                </span>
                <span v-else>No {{ searchDemoMode ? 'searches' : 'quotes' }} found</span>
            </div>

            <div class="d-flex align-items-center">
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1"
                >
                    Previous
                </button>
                <span class="mx-3"
                    >Page <strong>{{ currentPage }}</strong> of
                    <strong>{{ totalPages }}</strong></span
                >
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                >
                    Next
                </button>
            </div>
        </div>

        <transition name="details-slide">
            <aside
                v-if="!searchDemoMode && detailsVisible"
                class="details-sidebar"
                role="dialog"
                aria-modal="true"
                ref="detailsSidebar"
                tabindex="-1"
                @click.stop
                @keyup.esc="closeDetails"
            >
                <div class="details-sidebar__header">
                    <div>
                        <p class="details-sidebar__eyebrow">Quote ID</p>
                        <h3 class="details-sidebar__title">
                            {{ selectedRow ? selectedRow.id : '—' }}
                        </h3>
                        <div v-if="selectedRow" class="details-sidebar__badges">
                            <span
                                class="status-pill"
                                :class="
                                    isResearchQuote(selectedRow)
                                        ? 'status-pill--info'
                                        : 'status-pill--neutral'
                                "
                            >
                                <span class="status-pill__icon">
                                    <font-awesome-icon
                                        :icon="[
                                            'fas',
                                            isResearchQuote(selectedRow)
                                                ? 'flask'
                                                : 'clipboard-list',
                                        ]"
                                    />
                                </span>
                                {{
                                    isResearchQuote(selectedRow)
                                        ? 'Research Quote'
                                        : 'Standard Quote'
                                }}
                            </span>
                            <span
                                class="status-pill"
                                :class="statusPillClass(selectedRow && selectedRow.search_finished)"
                            >
                                <span class="status-pill__icon">
                                    <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                                </span>
                                {{
                                    statusLabel(
                                        'Search',
                                        selectedRow && selectedRow.search_finished
                                    )
                                }}
                            </span>
                            <span
                                class="status-pill"
                                :class="statusPillClass(selectedRow && selectedRow.quote_finished)"
                            >
                                <span class="status-pill__icon">
                                    <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" />
                                </span>
                                {{
                                    statusLabel('Quote', selectedRow && selectedRow.quote_finished)
                                }}
                            </span>
                        </div>
                    </div>
                    <button type="button" class="details-sidebar__close" @click="closeDetails">
                        <span aria-hidden="true">&times;</span>
                        <span class="visually-hidden">Close</span>
                    </button>
                </div>
                <div class="details-sidebar__content">
                    <dl>
                        <div v-for="field in detailFields" :key="field.key" class="detail-row">
                            <dt>{{ field.label }}</dt>
                            <dd>
                                <template
                                    v-if="selectedRow && Array.isArray(selectedRow[field.key])"
                                >
                                    <ul class="detail-list">
                                        <li
                                            v-for="(item, idx) in selectedRow[field.key]"
                                            :key="idx"
                                        >
                                            {{ item }}
                                        </li>
                                    </ul>
                                </template>
                                <template v-else>{{ formatDetailValue(field.key) }}</template>
                            </dd>
                        </div>
                    </dl>
                </div>
            </aside>
        </transition>
        <transition name="details-fade">
            <div
                v-if="!searchDemoMode && detailsVisible"
                class="details-backdrop"
                @click="closeDetails"
            ></div>
        </transition>
    </div>
</template>

<script>
    import { Modal } from 'bootstrap'
    import EditQuoteModal from '../../components/modals/EditQuoteModal.vue'
    import { formatMoneyValue } from '@/helpers/string.js'
    import { formatCalendarDateToUS } from '@/helpers/dates.js'
    export default {
        name: 'ResponsibleDataTable',
        props: {
            headers: {
                type: Object,
                required: true,
            },
            data: {
                type: Array,
                required: true,
            },
            serverPaging: {
                type: Boolean,
                default: false,
            },
            totalItems: {
                type: Number,
                default: null,
            },
            initialPage: {
                type: Number,
                default: 1,
            },
            initialPageSize: {
                type: Number,
                default: 10,
            },
            searchDemoMode: {
                type: Boolean,
                default: false,
            },
            showLiveSearchStatus: { type: Boolean, default: false },
        },
        components: {
            EditQuoteModal,
        },
        watch: {
            initialPage(newVal) {
                if (Number.isInteger(newVal) && newVal > 0) {
                    this.currentPage = newVal
                }
            },
            initialPageSize(newVal) {
                if (Number.isInteger(newVal) && newVal > 0) {
                    this.itemsPerPage = newVal
                }
            },
        },
        data() {
            return {
                sortColumn: null,
                sortDirection: 'asc',
                currentPage: this.initialPage,
                itemsPerPage: this.initialPageSize,
                selectedIds: [],
                expandedFields: {},
                detailsVisible: false,
                selectedRow: null,
                quoteId: 0,
                currentQuoteType: '',
            }
        },
        computed: {
            selectAll: {
                get() {
                    return (
                        this.selectedIds.length > 0 && this.selectedIds.length === this.data.length
                    )
                },
                set(value) {
                    if (value) {
                        this.selectedIds = this.data.map((item) => item.id)
                    } else {
                        this.selectedIds = []
                    }
                    this.emitSelection()
                },
            },
            sortedData() {
                if (!this.sortColumn) return this.data
                return this.data.slice().sort((a, b) => {
                    const valueA = a[this.sortColumn]
                    const valueB = b[this.sortColumn]
                    if (typeof valueA === 'number' && typeof valueB === 'number') {
                        return this.sortDirection === 'asc' ? valueA - valueB : valueB - valueA
                    } else {
                        return this.sortDirection === 'asc'
                            ? String(valueA).localeCompare(String(valueB))
                            : String(valueB).localeCompare(String(valueA))
                    }
                })
            },
            paginatedData() {
                if (this.serverPaging) {
                    return this.sortedData
                }
                const start = (this.currentPage - 1) * this.itemsPerPage
                return this.sortedData.slice(start, start + this.itemsPerPage)
            },
            detailFields() {
                const headerEntries = Object.entries(this.headers || {})
                if (headerEntries.length > 0) {
                    return headerEntries.map(([key, label]) => ({
                        key,
                        label: label || key,
                    }))
                }

                const sample = this.data?.[0] || {}
                return Object.keys(sample).map((key) => ({
                    key,
                    label: key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
                }))
            },
            displayedTotalItems() {
                const hasServerTotal = Number.isFinite(this.totalItems)
                if (this.serverPaging && hasServerTotal) {
                    return this.totalItems
                }
                return this.data.length
            },
            pageStart() {
                if (this.displayedTotalItems === 0) return 0
                return (this.currentPage - 1) * this.itemsPerPage + 1
            },
            pageEnd() {
                if (this.displayedTotalItems === 0) return 0
                return Math.min(this.currentPage * this.itemsPerPage, this.displayedTotalItems)
            },
            totalPages() {
                const total = this.displayedTotalItems
                return Math.max(1, Math.ceil(total / this.itemsPerPage))
            },
        },
        methods: {
            triggerEditQuote(searchActionId, quoteType) {
                this.quoteId = searchActionId
                this.currentQuoteType = quoteType

                const modalElement = document.getElementById('editQuoteModal')
                if (modalElement) {
                    const modal = Modal.getInstance(modalElement) || new Modal(modalElement)
                    modal.show()
                }
            },
            sortBy(column) {
                if (this.sortColumn === column) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = column
                    this.sortDirection = 'asc'
                }
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                    if (this.serverPaging) {
                        this.$emit('page-changed', page)
                    }
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
                if (this.serverPaging) {
                    this.$emit('page-size-changed', this.itemsPerPage)
                }
            },
            formatAmericanDate(dateString) {
                if (!dateString) return ''
                const date = new Date(dateString)
                if (isNaN(date)) return dateString
                const month = (date.getMonth() + 1).toString().padStart(2, '0')
                const day = date.getDate().toString().padStart(2, '0')
                const year = date.getFullYear()
                return `${month}/${day}/${year}`
            },
            formatDueDate(value) {
                return formatCalendarDateToUS(value)
            },
            formatQuoteValue(value) {
                return formatMoneyValue(value)
            },
            toggleSelectAll() {
                this.selectAll = !this.selectAll
            },
            emitSelection() {
                this.$emit('selection-changed', this.selectedIds)
            },
            clearSelection() {
                this.selectedIds = []
                this.emitSelection()
            },
            isResearchQuote(row) {
                const type = row?.quote_type
                if (!type) return false
                return String(type).toLowerCase().includes('research')
            },
            normalizeBoolean(value) {
                if (typeof value === 'string') {
                    return value.toLowerCase() === 'true' || value === '1'
                }
                return Boolean(value)
            },
            statusButtonClass(value) {
                return this.normalizeBoolean(value) ? 'finished' : 'not-finished'
            },
            statusLabel(label, value) {
                const completed = this.normalizeBoolean(value)
                return completed ? `${label} Finished` : `${label} Not Finished`
            },
            statusPillClass(value) {
                return this.normalizeBoolean(value)
                    ? 'status-pill--positive'
                    : 'status-pill--warning'
            },
            expandKey(row, field) {
                return `${row?.id ?? 'row'}-${field}`
            },
            listItems(row, field) {
                const list = row?.[field]
                return Array.isArray(list) ? list : []
            },
            listTotal(row, field) {
                return this.listItems(row, field).length
            },
            hiddenItemCount(row, field) {
                const total = this.listTotal(row, field)
                return Math.max(total - 8, 0)
            },
            isExpanded(row, field) {
                const key = this.expandKey(row, field)
                return Boolean(this.expandedFields[key])
            },
            toggleExpanded(row, field) {
                const key = this.expandKey(row, field)
                this.expandedFields = {
                    ...this.expandedFields,
                    [key]: !this.expandedFields[key],
                }
            },
            limitedItems(row, field) {
                const items = this.listItems(row, field)
                return this.isExpanded(row, field) ? items : items.slice(0, 8)
            },
            hasOverflow(row, field) {
                return this.listItems(row, field).length > 8
            },
            openDetails(row) {
                this.selectedRow = row
                this.detailsVisible = true
                this.$nextTick(() => {
                    this.$refs.detailsSidebar?.focus()
                })
            },
            closeDetails() {
                this.detailsVisible = false
                this.selectedRow = null
            },
            formatDetailValue(fieldKey) {
                if (!this.selectedRow) {
                    return '—'
                }
                const value = this.selectedRow[fieldKey]
                if (value === null || value === undefined || value === '') {
                    return '—'
                }

                if (Array.isArray(value)) {
                    return value.join(', ')
                }

                const keyLower = fieldKey ? fieldKey.toLowerCase() : ''
                if (fieldKey === 'total_value') {
                    return formatMoneyValue(value)
                }

                if (fieldKey === 'due_date') {
                    return this.formatDueDate(value)
                }

                if (
                    typeof value === 'string' &&
                    (keyLower.includes('date') || keyLower.includes('time'))
                ) {
                    return this.formatAmericanDate(value)
                }

                if (typeof value === 'boolean') {
                    return value ? 'Yes' : 'No'
                }

                if (typeof value === 'object') {
                    try {
                        return JSON.stringify(value, null, 2)
                    } catch (error) {
                        return String(value)
                    }
                }

                return value
            },
        },
    }
</script>

<style scoped lang="scss">
    body {
        background-color: var(--color-app-bg);
    }

    table th,
    table td {
        text-align: center;
    }

    table tr:nth-child(even) {
        background-color: var(--color-surface-canvas);
    }

    .table-xs {
        margin: 0 auto;
    }

    .table-xs > div {
        padding: 20px;
        text-align: center;
        border: 1px solid var(--color-surface-canvas);
        word-break: break-word;
    }

    .row-header {
        overflow: hidden;
    }

    .table-xs .header {
        font-weight: 600;
        background-color: var(--color-surface-canvas);
        font-weight: 600;
    }

    .visible-sm-block {
        display: none !important;
    }

    .hidden-sm .hidden-xs {
        display: block !important;
    }

    @media (max-width: 855px) and (min-width: 576px) {
        .visible-sm-block {
            display: block !important;
        }

        .hidden-sm,
        .hidden-xs {
            display: none !important;
        }
    }

    .badge-danger {
        color: var(--color-text-inverse);
        background-color: var(--color-brand-primary);
    }

    .badge {
        display: inline-block;
        padding: 0.25em 0.4em;
        font-size: 75%;
        font-weight: 700;
        line-height: 1;
        text-align: center;
        white-space: nowrap;
        vertical-align: baseline;
        border-radius: 0.25rem;
    }
    .table {
        thead {
            td,
            th {
                color: var(--color-text-inverse);
                background-color: var(--color-surface-inverse) !important;
            }
        }
    }
    .link-button {
        background: none;
        border: none;
        padding: 0;
        margin-left: 4px;
        color: var(--color-brand-primary);
        font-weight: bold;
        cursor: pointer;
        font-size: 0.85rem;
    }
    .parts-total {
        display: inline-block;
        margin-left: 6px;
        padding: 1px 4px;
        background-color: var(--color-surface-inverse);
        color: var(--color-text-inverse);
        font-size: 0.75rem;
        line-height: 1.2;
        font-weight: 400;
    }
    .id-cell {
        display: flex;
        flex-direction: column;
        align-items: center;
        white-space: nowrap;
        width: 76px;
    }
    .id-value {
        display: inline-flex;
        align-items: center;
        padding: 2px 6px;
        border-radius: 6px;
        width: 100%;
        justify-content: center;
        cursor: pointer;
        transition:
            background-color 0.2s ease,
            color 0.2s ease,
            box-shadow 0.2s ease;
        &:hover {
            background-color: var(--color-brand-primary-soft);
        }
        &:focus-visible {
            outline: none;
            box-shadow: var(--shadow-focus-brand);
        }
    }
    .id-value.research-quote {
        /*background-color: rgb(219, 234, 254);*/
        color: var(--color-state-info);
        cursor: pointer;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }
    .id-value--static {
        cursor: default;
        color: var(--color-text-primary);
        &:hover {
            background-color: transparent;
        }
    }
    .research-indicator {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 4px;
    }
    .quote-status-group {
        width: 76px;
    }
    .quote-status-group--search-only {
        justify-content: center;
    }
    .btn-status {
        min-width: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: default;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        border: none;
        background-color: inherit;
        position: relative;
        overflow: hidden;
        font-size: 17px;
        &.finished {
            color: var(--color-state-success);
        }
        &.not-finished {
            color: var(--color-state-warning);
            &::after {
                content: '';
                position: absolute;
                inset: 2px;
                border-top: 2px dashed var(--color-state-warning);
                transform: translateY(7px) rotate(-37deg);
                transform-origin: top;
                pointer-events: none;
            }
        }
    }
    .btn-quote {
        border-left: 1px solid var(--color-border-default);
    }
    .btn-search-interactive { cursor: pointer; }
    .btn-search-interactive:hover { background-color: var(--bs-tertiary-bg, #eee); }
    .btn-search-interactive:focus-visible { outline: 2px solid currentColor; outline-offset: -2px; }
    .parts-list {
        max-width: 350px;
    }
    .sort-buttons {
        width: 56px;
    }
    .btn-sort {
        background: none;
        border: none;
        padding: 0;
        margin-left: 4px;
        color: #fff;
        cursor: pointer;
        &:first-child {
            margin-right: 5px;
        }
    }
    .sortable-header {
        cursor: pointer;
    }
    .table-top-bar {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        gap: 12px;
        flex-wrap: wrap;
        margin-bottom: 6px;
    }
    .legend-trigger {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 1px;
        margin-bottom: 1px;
        color: var(--color-text-inverse);
        cursor: help;
        background-color: var(--color-brand-primary);
        font-size: 13px;
        padding: 0px 10px;
        border-top-left-radius: 7px;
        border-top-right-radius: 7px;
        position: relative;
        .legend-trigger-wrapper {
            margin-left: 4px;
        }
    }
    .legend-trigger:focus-visible {
        outline: 2px solid var(--color-state-warning-soft);
        outline-offset: 2px;
    }
    .legend-popover {
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(50%, -6px);
        min-width: 220px;
        background: var(--color-surface-canvas);
        border-radius: 10px;
        box-shadow: var(--shadow-lg);
        padding: 0.75rem;
        font-size: 0.75rem;
        color: var(--color-text-primary);
        opacity: 0;
        pointer-events: none;
        transition:
            opacity 0.15s ease,
            transform 0.15s ease;
        z-index: 10;
    }
    .legend-trigger:hover .legend-popover,
    .legend-trigger:focus-within .legend-popover {
        opacity: 1;
        pointer-events: auto;
        transform: translate(100%, -5px);
    }
    .table-summary {
        text-align: center;
        flex: 1 1 auto;
        min-width: 260px;
    }
    .legend-title {
        margin: 0 0 0.35rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
    }
    .legend-popover ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
    }
    .legend-popover li {
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }
    .legend-popover li span {
        display: inline-flex;
        align-items: center;
        background-color: inherit;
    }
    .text-finished {
        color: var(--color-state-success);
    }
    .text-pending {
        color: var(--color-state-warning);
    }
    .legend__research-quote {
        color: var(--color-state-info);
    }
    .legend__standard-quote {
        display: none !important;
    }
    /* Mobile: */
    @media (max-width: 576px) {
        /* ... */
    }

    /* Tablet */
    @media (min-width: 577px) and (max-width: 768px) {
        /* ... */
    }

    /* Small Devices */
    @media (min-width: 769px) and (max-width: 992px) {
        /* ... */
    }

    /* Larger Devices */
    @media (min-width: 993px) {
        /* ... */
    }

    .details-backdrop {
        position: fixed;
        inset: 0;
        background: var(--color-surface-overlay);
        z-index: 1040;
    }

    .details-sidebar {
        position: fixed;
        top: 0;
        right: 0;
        width: 380px;
        max-width: 90vw;
        height: 100vh;
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
        box-shadow: var(--shadow-xl);
        z-index: 1050;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
    }

    .details-sidebar__header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1.5rem;
        gap: 1rem;
    }

    .details-sidebar__eyebrow {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin: 0 0 0.25rem;
        color: var(--color-text-muted);
    }

    .details-sidebar__title {
        margin: 0;
        font-size: 1.75rem;
        font-weight: 600;
        color: var(--color-text-primary);
    }

    .details-sidebar__close {
        border: none;
        background: none;
        font-size: 1.75rem;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        color: var(--color-text-secondary);
    }

    .details-sidebar__content {
        flex: 1;
    }

    .details-sidebar__badges {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
        margin-top: 0.75rem;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.15rem 0.65rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: var(--color-text-secondary);
        background: var(--color-neutral-soft);
        position: relative;
        overflow: hidden;
    }

    .status-pill__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.85rem;
        position: relative;
    }

    .status-pill--info {
        color: var(--color-state-info);
        background: var(--color-info-soft);
    }

    .status-pill--neutral {
        color: var(--color-text-secondary);
        background: var(--color-neutral-soft);
    }

    .status-pill--positive {
        color: var(--color-state-success);
        background: rgba(16, 185, 129, 0.25);
    }

    .status-pill--warning {
        color: var(--color-state-warning);
        background: var(--color-state-warning-soft);
        .status-pill__icon {
            &::after {
                content: '';
                position: absolute;
                inset: -7px;
                border-top: 2px dashed var(--color-state-warning);
                transform: translateX(7px) translateY(9px) rotate(-35deg);
                pointer-events: none;
            }
        }
    }

    .detail-row {
        margin-bottom: 1.25rem;
        padding-bottom: 1.25rem;
        border-bottom: 1px solid var(--color-border-muted);

        &:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }
    }

    .detail-row dt {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin: 0 0 0.25rem;
        color: var(--color-text-muted);
    }

    .detail-row dd {
        margin: 0;
        font-weight: 500;
        color: var(--color-text-primary);
        white-space: pre-wrap;
        word-break: break-word;
    }

    .detail-list {
        margin: 0;
        padding-left: 1.25rem;
        list-style: disc;
    }

    .details-slide-enter-active,
    .details-slide-leave-active {
        transition:
            transform 0.3s ease,
            opacity 0.3s ease;
    }

    .details-slide-enter-from,
    .details-slide-leave-to {
        transform: translateX(100%);
        opacity: 0;
    }

    .details-fade-enter-active,
    .details-fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .details-fade-enter-from,
    .details-fade-leave-to {
        opacity: 0;
    }
</style>
